// components/account/AccountHistoricsModal/hooks/useAccountHistorics.js
import { useState, useEffect, useCallback } from 'react';
import AccountHistoricDocumentService from '../../api/Documents/AccountHistoricDocumentService';

/**
 * Hook personnalisé pour gérer les données des historiques d'un compte
 * @param {Object} account - Le compte à analyser
 * @param {number} domainId - ID du domaine
 * @param {boolean} show - Si le modal est visible
 */
export const useAccountHistorics = (account, domainId, show) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [filteredHistorics, setFilteredHistorics] = useState([]);
  
  // États des filtres
  const [filters, setFilters] = useState({
    searchTerm: '',
    containerFilter: '',
    categoryFilter: '',
    dateRange: { start: '', end: '' },
    hasDocument: null // null = tous, true = avec doc, false = sans doc
  });

  /**
   * Charge les données des historiques depuis l'API
   */
  const fetchHistorics = useCallback(async () => {
    if (!account || !domainId) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await AccountHistoricDocumentService.getAccountHistoricsWithDocuments(
        account.id, 
        domainId
      );
      
      setData(response);
      setFilteredDocuments(response.documents || []);
      setFilteredHistorics(response.allHistorics || []);
    } catch (err) {
      // console.error('Erreur chargement historiques:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des historiques');
    } finally {
      setLoading(false);
    }
  }, [account, domainId]);

  /**
   * Applique les filtres aux données
   */
  const applyFilters = useCallback(() => {
    if (!data) return;

    // Filtrer les historiques
    let filteredHist = data.allHistorics || [];
    let filteredDocs = data.documents || [];

    const { searchTerm, containerFilter, categoryFilter, dateRange, hasDocument } = filters;

    // Filtre par recherche textuelle
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredHist = filteredHist.filter(hist => 
        hist.name.toLowerCase().includes(term) ||
        hist.categorie.toLowerCase().includes(term) ||
        hist.descriptif.toLowerCase().includes(term)
      );
      filteredDocs = filteredDocs.filter(doc =>
        doc.name.toLowerCase().includes(term) ||
        doc.categorie.toLowerCase().includes(term) ||
        doc.descriptif.toLowerCase().includes(term)
      );
    }

    // Filtre par container (documents uniquement)
    if (containerFilter) {
      filteredDocs = filteredDocs.filter(doc => doc.container === containerFilter);
    }

    // Filtre par catégorie
    if (categoryFilter) {
      filteredHist = filteredHist.filter(hist => hist.categorie === categoryFilter);
      filteredDocs = filteredDocs.filter(doc => doc.categorie === categoryFilter);
    }

    // Filtre par date
    if (dateRange.start) {
      const startDate = new Date(dateRange.start);
      filteredHist = filteredHist.filter(hist => new Date(hist.date) >= startDate);
      filteredDocs = filteredDocs.filter(doc => new Date(doc.date) >= startDate);
    }
    
    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      filteredHist = filteredHist.filter(hist => new Date(hist.date) <= endDate);
      filteredDocs = filteredDocs.filter(doc => new Date(doc.date) <= endDate);
    }

    // Filtre par présence de document
    if (hasDocument !== null) {
      filteredHist = filteredHist.filter(hist => hist.hasDocument === hasDocument);
    }

    setFilteredHistorics(filteredHist);
    setFilteredDocuments(filteredDocs);
  }, [data, filters]);

  /**
   * Met à jour un filtre spécifique
   */
  const updateFilter = useCallback((filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  }, []);

  /**
   * Réinitialise tous les filtres
   */
  const resetFilters = useCallback(() => {
    setFilters({
      searchTerm: '',
      containerFilter: '',
      categoryFilter: '',
      dateRange: { start: '', end: '' },
      hasDocument: null
    });
  }, []);

  // Charger les données quand le modal s'ouvre
  useEffect(() => {
    if (show && account && domainId) {
      fetchHistorics();
    }
  }, [show, account, domainId, fetchHistorics]);

  // Appliquer les filtres quand ils changent
  useEffect(() => {
    applyFilters();
  }, [applyFilters, filters]);

  return {
    loading,
    error,
    data,
    filteredDocuments,
    filteredHistorics,
    filters,
    updateFilter,
    resetFilters,
    refetch: fetchHistorics
  };
};